import React, { useEffect, useRef } from 'react';

/**
 * Mathematically accurate moon-phase visual rendered via HTML Canvas.
 *
 * Rationale for Canvas over SVG:
 *   Aggressive OS-level "Force Dark" implementations (Xiaomi MIUI, Samsung
 *   One UI, Huawei EMUI, and Chrome's `chrome://flags/#enable-force-dark`)
 *   IGNORE the `color-scheme` meta tag, `forced-color-adjust`, and every
 *   CSS opt-out for SVG. They inspect the rendered DOM and invert any
 *   light-coloured element to keep the whole screen dark.
 *
 *   Canvas pixel data, however, is treated as opaque image content by every
 *   Android/Chromium force-dark pipeline — the pixels are NEVER inverted.
 *   This is the ONLY reliable technique that guarantees a bright, correctly
 *   illuminated moon on every device, regardless of the user's system
 *   dark-mode settings.
 *
 * Terminator math (unchanged from the previous SVG implementation):
 *   illumination = (1 - cos(phase_angle)) / 2
 *   => cos(phase_angle) = 1 - 2 * illumination
 *   => ellipse_rx = |1 - 2 * illumination| * r
 */
const MoonPhaseVisual = ({ illumination = 0.5, ageDays = 14.77, size = 160 }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = window.devicePixelRatio || 1;
    canvas.width = size * dpr;
    canvas.height = size * dpr;
    canvas.style.width = `${size}px`;
    canvas.style.height = `${size}px`;

    const ctx = canvas.getContext('2d');
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, size, size);

    const cx = size / 2;
    const cy = size / 2;
    const r = size / 2 - 1;

    const i = Math.max(0, Math.min(1, illumination));
    const isWaxing = ageDays < 14.77;
    const k = 1 - 2 * i;
    const rx = Math.abs(k) * r;

    /* ── 1. Dark side (full disk background) ─────────────────── */
    const darkGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    darkGrad.addColorStop(0, '#1F2937');
    darkGrad.addColorStop(1, '#0F1729');
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = darkGrad;
    ctx.fill();

    /* ── 2. Lit region (terminator-correct path) ─────────────── */
    // Canvas Path2D accepts SVG path strings.
    const sweepOuter = isWaxing ? 1 : 0;
    const sweepInner = isWaxing ? (i < 0.5 ? 0 : 1) : (i < 0.5 ? 1 : 0);

    const litPath = new Path2D(
      `M ${cx} ${cy - r} ` +
      `A ${r} ${r} 0 0 ${sweepOuter} ${cx} ${cy + r} ` +
      `A ${rx} ${r} 0 0 ${sweepInner} ${cx} ${cy - r} Z`
    );

    // Radial gradient for subtle limb darkening on the lit portion.
    const litGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, r);
    litGrad.addColorStop(0.00, 'rgba(255, 255, 255, 1)');
    litGrad.addColorStop(0.78, 'rgba(232, 232, 232, 0.94)');
    litGrad.addColorStop(1.00, 'rgba(156, 163, 175, 0.85)');
    ctx.fillStyle = litGrad;
    ctx.fill(litPath);

    /* ── 3. Soft craters (clipped to the disk) ───────────────── */
    ctx.save();
    ctx.beginPath();
    ctx.arc(cx, cy, r - 0.5, 0, Math.PI * 2);
    ctx.clip();

    const drawCrater = (px, py, cr, alpha) => {
      // Scale from viewBox (100) → canvas (size)
      const rx = (px / 100) * size;
      const ry = (py / 100) * size;
      const rr = (cr / 100) * size;
      const g = ctx.createRadialGradient(rx, ry, 0, rx, ry, rr);
      g.addColorStop(0, `rgba(120,120,120,${alpha})`);
      g.addColorStop(1, 'rgba(120,120,120,0)');
      ctx.beginPath();
      ctx.arc(rx, ry, rr, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
    };
    drawCrater(34, 40, 8, 0.45);
    drawCrater(62, 58, 6, 0.35);
    drawCrater(46, 72, 5, 0.45);
    drawCrater(68, 32, 4, 0.35);
    ctx.restore();

    /* ── 4. Outer rim shadow ─────────────────────────────────── */
    ctx.beginPath();
    ctx.arc(cx, cy, r - 0.5, 0, Math.PI * 2);
    ctx.strokeStyle = 'rgba(0, 0, 0, 0.35)';
    ctx.lineWidth = 1.2;
    ctx.stroke();
  }, [illumination, ageDays, size]);

  return (
    <div
      style={{
        width: size,
        height: size,
        position: 'relative',
        filter: `drop-shadow(0 0 ${size * 0.18}px rgba(232, 232, 232, 0.45))`,
        flexShrink: 0,
        // Defensive: also opt out of forced-dark on the container.
        colorScheme: 'dark',
        forcedColorAdjust: 'none',
        WebkitForcedColorAdjust: 'none',
      }}
      data-testid="moon-visual"
      aria-label={`Moon illumination ${(illumination * 100).toFixed(1)}%`}
    >
      <canvas
        ref={canvasRef}
        style={{
          display: 'block',
          width: '100%',
          height: '100%',
        }}
      />
    </div>
  );
};

export default MoonPhaseVisual;
